import axiosInstance from './axiosInstance';

/**
 * API functions for vehicle analytics and reporting
 */

/**
 * Get dashboard summary metrics
 * @param {string} location - Optional location filter
 */
export const getDashboardMetrics = async (location) => {
  try {
    const params = location ? { location } : {};
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/dashboard', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get vehicle report for a specific vehicle
 * @param {string} vehicleId - Vehicle UUID
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 */
export const getVehicleReport = async (vehicleId, startDate, endDate) => {
  try {
    const response = await axiosInstance.get(
      `/Corrohealth/vehicles/analytics/report/${vehicleId}`,
      {
        params: { startDate, endDate }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get reports for all vehicles
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {string} location - Optional location filter
 */
export const getAllVehicleReports = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;

    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/reports', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get usage chart data for visualizations
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {string} location - Optional location filter
 */
export const getUsageCharts = async (startDate, endDate, location) => {
  try {
    const params = { startDate, endDate };
    if (location) params.location = location;

    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/charts', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Log a vehicle usage event
 * @param {Object} usageLog - Vehicle usage log data
 */
export const logVehicleUsage = async (usageLog) => {
  try {
    const response = await axiosInstance.post('/Corrohealth/vehicles/analytics/log', usageLog);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get usage logs for a specific vehicle
 * @param {string} vehicleId - Vehicle UUID
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 */
export const getVehicleUsageLogs = async (vehicleId, startDate, endDate) => {
  try {
    const response = await axiosInstance.get(
      `/Corrohealth/vehicles/analytics/logs/${vehicleId}`,
      {
        params: { startDate, endDate }
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all usage logs within date range
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 */
export const getAllUsageLogs = async (startDate, endDate) => {
  try {
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/logs', {
      params: { startDate, endDate }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user-based vehicle analytics with capacity tracking
 * @param {string} location - Optional location filter
 */
export const getUserAnalytics = async (location) => {
  try {
    const params = location ? { location } : {};
    const response = await axiosInstance.get('/Corrohealth/vehicles/analytics/user-analytics', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default {
  getDashboardMetrics,
  getVehicleReport,
  getAllVehicleReports,
  getUsageCharts,
  logVehicleUsage,
  getVehicleUsageLogs,
  getAllUsageLogs,
  getUserAnalytics,
};
